import { Injectable } from '@angular/core';
import { Chart, ChartType, TooltipModel } from 'chart.js';

export interface ChartTooltipLine {
  indicatorColor?: string;
  label?: string;
  value: string;
  extra?: string;
}

export interface ChartTooltipContent {
  title?: string;
  lines: ChartTooltipLine[];
}

export interface ChartTooltipRenderPayload<TType extends ChartType> {
  chart: Chart<TType>;
  tooltip: TooltipModel<TType>;
}

export interface ChartTooltipManagerOptions<TType extends ChartType> {
  render: (
    payload: ChartTooltipRenderPayload<TType>
  ) => ChartTooltipContent | null;
  transform?: string;
}

export interface ChartTooltipManager<TType extends ChartType> {
  handler: (payload: {
    chart: Chart<TType>;
    tooltip: TooltipModel<TType>;
  }) => void;
  hide(): void;
  destroy(): void;
}

@Injectable({
  providedIn: 'root',
})
export class ChartTooltipService {
  createManager<TType extends ChartType>(
    options: ChartTooltipManagerOptions<TType>
  ): ChartTooltipManager<TType> {
    if (typeof document === 'undefined') {
      const noop = () => {};
      return {
        handler: noop as ChartTooltipManager<TType>['handler'],
        hide: noop,
        destroy: noop,
      };
    }

    const el = this._createTooltipElement();
    const transform =
      options.transform ?? 'translate(-50%, calc(-100% - 12px))';

    const handler = ({
      chart,
      tooltip,
    }: {
      chart: Chart<TType>;
      tooltip: TooltipModel<TType>;
    }) => {
      if (!tooltip || tooltip.opacity === 0) {
        this._hide(el);
        return;
      }

      const content = options.render({ chart, tooltip });
      if (!content || !content.lines?.length) {
        this._hide(el);
        return;
      }

      this._updateContent(el, content);

      const canvasRect = chart.canvas.getBoundingClientRect();
      const left = canvasRect.left + window.scrollX + tooltip.caretX;
      const top = canvasRect.top + window.scrollY + tooltip.caretY;

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.transform = transform;
      el.style.opacity = '1';
    };

    return {
      handler,
      hide: () => this._hide(el),
      destroy: () => {
        this._hide(el);
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      },
    };
  }

  private _createTooltipElement(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'chart-tooltip';
    Object.assign(el.style, {
      position: 'absolute',
      background: '#1f2933',
      color: '#f5f7fa',
      padding: '8px 12px',
      borderRadius: '8px',
      boxShadow: '0 8px 16px rgba(15, 23, 42, 0.35)',
      fontFamily: 'Poppins, sans-serif',
      fontSize: '12px',
      lineHeight: '16px',
      zIndex: '1100',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 120ms ease-out',
      maxWidth: '220px',
    });

    el.innerHTML = `
      <div class="chart-tooltip__title" style="font-weight:600;font-size:12px;margin-bottom:4px;"></div>
      <div class="chart-tooltip__lines" style="display:flex;flex-direction:column;gap:6px;"></div>
    `;

    document.body.appendChild(el);
    return el;
  }

  private _updateContent(
    el: HTMLDivElement,
    content: ChartTooltipContent
  ): void {
    const titleEl = el.querySelector(
      '.chart-tooltip__title'
    ) as HTMLDivElement | null;
    const linesContainer = el.querySelector(
      '.chart-tooltip__lines'
    ) as HTMLDivElement | null;

    if (titleEl) {
      if (content.title) {
        titleEl.textContent = content.title;
        titleEl.style.display = 'block';
      } else {
        titleEl.textContent = '';
        titleEl.style.display = 'none';
      }
    }

    if (!linesContainer) return;

    linesContainer.innerHTML = '';

    for (const line of content.lines) {
      const lineEl = document.createElement('div');
      Object.assign(lineEl.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      });

      if (line.indicatorColor) {
        const indicator = document.createElement('span');
        Object.assign(indicator.style, {
          display: 'inline-block',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: line.indicatorColor,
        });
        lineEl.appendChild(indicator);
      }

      const textWrapper = document.createElement('div');
      Object.assign(textWrapper.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: line.extra ? '2px' : '0px',
      });

      if (line.label) {
        const labelEl = document.createElement('div');
        Object.assign(labelEl.style, {
          fontSize: '12px',
          color: '#d9e2ec',
        });
        labelEl.textContent = line.label;
        textWrapper.appendChild(labelEl);
      }

      const valueEl = document.createElement('div');
      Object.assign(valueEl.style, {
        fontSize: '13px',
        fontWeight: '600',
        color: '#f5f7fa',
      });
      valueEl.textContent = line.value;
      textWrapper.appendChild(valueEl);

      if (line.extra) {
        const extraEl = document.createElement('div');
        Object.assign(extraEl.style, {
          fontSize: '11px',
          color: '#bcccdc',
        });
        extraEl.textContent = line.extra;
        textWrapper.appendChild(extraEl);
      }

      lineEl.appendChild(textWrapper);
      linesContainer.appendChild(lineEl);
    }
  }

  private _hide(el: HTMLDivElement): void {
    el.style.opacity = '0';
  }
}
